import { Box, useTheme } from "@mui/material";
import { AnnotatedTextField } from "./AnnotatedTextField";
import { IViewport } from "../../FacilityManager/Domain/Types/FloorPlan/Viewport.type";
import { IZoneUserNumber } from "../../FacilityManager/Domain/Types/FloorPlan/ZoneSchedule";
import { ButtonStyledText } from "../ButtonStyledText.partial";

export type MiniTextInputField = {
  id: string;
  placeholder: string;
  value: string | number | null;
  type: "text" | "number";
};
interface Props {
  fields: MiniTextInputField[];
  onInput?: (id: string, value: string | number) => void;
  // bounding box for place property
  newBoundingBox?: IViewport;
  setNewBoundingBox?: (v: IViewport) => void;
  handleOk?: (newValue: IViewport) => void;
  handleAssign?: () => void;
  // number of users for conference zone
  zoneUserNumber?: IZoneUserNumber;
  setZoneUserNumber?: (n: IZoneUserNumber) => void;
  readOnly?: boolean;
  buttonOkLabel?: string;
  buttonAssignLabel?: string;
  displayError?: boolean;
  buttonDataTestId?: string;
}

/**
 * a slim text input for small sidebars
 * it can consist of multiple fields that are glued together
 * @param fields all fields that should be created
 * @param onInput if any of the fields are edited, this emits the new value and id
 */
export function MiniTextInput({
  fields,
  newBoundingBox = { width: 160, height: 80 },
  onInput,
  handleOk,
  handleAssign,
  setNewBoundingBox,
  zoneUserNumber,
  setZoneUserNumber,
  readOnly = false,
  buttonOkLabel = "Ok",
  buttonAssignLabel = "Assign",
  displayError = false,
  buttonDataTestId
}: Readonly<Props>) {
  const theme = useTheme();

  return (
    <Box display={"flex"} sx={{ flexDirection: "row" }}>
      {/** Every field gets a text field with placeholder */}
      {fields.map((field, index) => (
        <AnnotatedTextField
          key={field.id}
          type={field.type}
          value={field.value}
          index={index}
          fieldLength={fields.length}
          placeholder={field.placeholder}
          onChange={newValue => {
            onInput?.(field.id, newValue);
            setNewBoundingBox?.({
              width: field.id === "width" ? Number(newValue) : newBoundingBox?.width,
              height: field.id === "height" ? Number(newValue) : newBoundingBox?.height
            });
            setZoneUserNumber?.({
              minUsers: field.id === "min" ? Number(newValue) : zoneUserNumber?.minUsers ?? 0,
              maxUsers: field.id === "max" ? Number(newValue) : zoneUserNumber?.maxUsers ?? null
            });
          }}
          readOnly={readOnly}
          displayError={displayError}
          buttonDataTestId={buttonDataTestId ?? "textfield-assign-btn"}
          handleAssign={handleAssign}
          buttonAssignLabel={buttonAssignLabel}
        />
      ))}
      {handleOk && !readOnly && (
        <ButtonStyledText
          theme={theme}
          data-testid={buttonDataTestId ?? "textfield-ok-btn"}
          sx={[
            { fontSize: 11, height: "inherit" },
            buttonOkLabel === "Ok" ? { minWidth: "30px" } : { minWidth: "fit-content" }
          ]}
          onClick={() => handleOk?.(newBoundingBox)}
        >
          {buttonOkLabel}
        </ButtonStyledText>
      )}
    </Box>
  );
}
